// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useGetSettings } from '@Queries/UISettingsQueries';
import { similarityFilters } from '@Constants/settings';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import {
    ObjectTypeV1 as ObjectType,
    AlertSimilarCasesOutput,
    AlertOutput,
    CaseOutput,
    CaseResolutionStatus,
    CaseStatusStage,
    CustomStatusMapType,
    getDateRange,
    PermissionsEnum,
} from 'thehive-sdk';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import RouteBuilder from '@Services/RouteBuilder';
import client from '@Client';
import { ICONS } from '@Constants/iconsApp';
import { AlertSimilarCasesStats } from '@Organisms/Alert/AlertSimilarCasesStats/AlertSimilarCasesStats';
import { FormatDate } from '@Molecules/Format';
import useConfigList from '@Hooks/useConfigList';
import { THLayout } from '@Molecules/THLayout';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import QuickFilterList from '@Molecules/QuickFilterList/QuickFilterList';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import CaseListTitle from '@Organisms/Case/CaseListTitle/CaseListTitle';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import THTag from '@Atoms/THTag/THTag';
import useOneQuery from '@Hooks/useOneQuery';
import { computeGetSelector } from '@Organisms/Alert/selector';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import ListProvider from '@Context/ListProvider';
import { RouteType } from '@Types';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { CaseDrawer } from '@Organisms/Case/CaseDrawer/CaseDrawer';
import { useDrawer } from '@Hooks/useDrawer';
import { computeEventList } from '@Organisms/Case/eventList';
import { actionList } from '@Organisms/Case/actionList';
import { ScopeContext } from '@Context/ScopeProvider';
import { useCurrentUser } from '@Queries/userQueries';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { Badge } from 'antd';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import usePageTitle from '@Hooks/usePageTitle';

import './SimilarCasesList.scss';

interface SimilarStateFilter {
    title: string;
    obsPercentage: number;
    iocPercentage: number;
    dataTypes: string[];
}

const defaultStateFilterValues: SimilarStateFilter = { dataTypes: [], iocPercentage: 0, obsPercentage: 0, title: '' };

const SimilarCasesList = (): ReactElement => {
    const i = useLocalMessage();

    const history = useHistory();
    const { alertId } = useParams<{ alertId: string }>();
    const [caseStatus, setCaseStatus] = React.useState<CustomStatusMapType>({});

    const { visible, setVisible, data, setData } = useDrawer<CaseOutput>({});

    const { data: settingsData } = useGetSettings();
    const { data: currentUser } = useCurrentUser();
    const { organisation: userOrganisation = '', permissions = [] } = currentUser || {};

    const defaultAlertSimilarCaseFilter = settingsData?.defaultAlertSimilarCaseFilter || '';
    const defaultFilter = useMemo(
        () =>
            defaultAlertSimilarCaseFilter
                ? (similarityFilters[defaultAlertSimilarCaseFilter]?.filters || []).map(
                      ({ field, type, operator, values, describeAttribute }) => {
                          if (type === 'date' && operator !== 'custom') {
                              values = getDateRange(operator);
                          }
                          return { field, type, operator, values, describeAttribute };
                      },
                  )
                : undefined,
        [defaultAlertSimilarCaseFilter],
    );
    const { data: alertData } = useOneQuery<AlertOutput>({
        selector: computeGetSelector({ alertId }),
        controller: client.AlertController,
        manual: true,
    });

    const config = useConfigList<AlertSimilarCasesOutput>({
        selector: {
            head: [
                {
                    name: 'getAlert',
                    idOrName: alertId,
                },
                {
                    name: 'similarCases',
                    caseFilter: {},
                },
            ],
            name: `get-alert-similar-cases-${alertId}`,
            defaultFilters: defaultFilter,
        },
        controller: client.AlertController,
        storageId: StorageFilterEnum.ALERT_SIMILAR_CASES,
        objectType: ObjectType.CASE,
        pagination: 'manual',
        fetchOnStart: !!defaultFilter,
        streamRootId: alertId,
    });

    const {
        dataSource = [],
        columnsRenderer,
        quickFilterListRenderer,
        storagePageManager,
        storageViewManager,
        status = 'loading',
        listRefetch,
        countRefetch,
        stateNeedRefresh: { needRefresh, setNeedRefresh },
        sortManager: { getFieldSort },
        paginationManager,
        filterManager,
        table: { count },
    } = config;

    const { page: storagePage, setFilterIsOpen, setAutoRefresh, setOnLoadView } = storagePageManager || { page: null };

    const [stateFilter, setStateFilter] = useState<SimilarStateFilter>({
        dataTypes: [],
        iocPercentage: 0,
        obsPercentage: 0,
        title: '',
    });

    const [localAutoRefresh, setLocalAutoRefresh] = useState(storagePage?.autoRefresh || false);
    const [localFilterIsOpen, setLocalFilterIsOpen] = useState(storagePage?.filterIsOpen || false);
    setOnLoadView?.((viewContent) => {
        setLocalAutoRefresh(viewContent?.autoRefresh || false);
    });

    const {
        customStatus: {
            case: { list: customStatusList },
        },
    } = useContext(ScopeContext);

    const observableTypesList = useMemo(
        () =>
            Array.from(
                new Set(
                    dataSource.reduce<string[]>(
                        (acc, { observableTypes }) => [...acc, ...Object.keys(observableTypes)],
                        [],
                    ),
                ),
            ),
        [dataSource],
    );

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.CaseController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (count, message) => {
                    if (count) {
                        countRefetch();
                    }
                    if (message) {
                        openMessage('success', message);
                    }
                    listRefetch();
                },
            }),
        [listRefetch, countRefetch, i],
    );

    const hasPermissions = useSplitPermissions(permissions, [
        PermissionsEnum.MANAGE_CASE_DELETE,
        PermissionsEnum.MANAGE_CASE_UPDATE,
        PermissionsEnum.MANAGE_ACTION,
    ]);

    const caseActionList = actionList({
        local: i,
        caseStatus,
        eventList,
        customStatusList,
        organisation: userOrganisation,
        onCloseBulkStatusManage: () => {
            listRefetch();
            countRefetch();
        },
        onEditBulkReopen: () => {
            listRefetch();
            countRefetch();
        },
        onDeleteBulk: () => {
            listRefetch();
            countRefetch();
        },
        hasPermissions,
        currentUserData: currentUser,
    });

    const quickFilterList = quickFilterListRenderer([
        {
            title: i('filter.quick.default'),
            onClick: ({ custom: filterCustom }): void => {
                defaultFilter?.map((filter) => filterCustom(filter));
            },
        },
        { divider: true },
        {
            title: i('filter.quick.similar-cases.open'),
            onClick: ({ custom }): void =>
                custom({ field: 'stage', operator: 'not', values: [CaseStatusStage.CLOSED] }),
        },
        {
            title: i('filter.quick.similar-cases.closed'),
            onClick: ({ custom }): void => custom({ field: 'stage', operator: 'eq', values: CaseStatusStage.CLOSED }),
        },
        { divider: true },
        {
            title: i('filter.quick.similar-cases.true-positive'),
            onClick: ({ custom }): void => {
                custom({ field: 'status', operator: 'eq', values: CaseResolutionStatus.TRUE_POSITIVE });
            },
        },
        {
            title: i('filter.quick.similar-cases.indeterminate'),
            onClick: ({ custom }): void => {
                custom({ field: 'status', operator: 'eq', values: CaseResolutionStatus.INDETERMINATE });
            },
        },
    ]);

    const columns = columnsRenderer([
        {
            title: [{ label: i('alert.similar-cases.table.title.label'), sortField: 'title' }],
            className: 'similar-cases-list__column__title',
            children: [
                {
                    title: (
                        <THInput
                            value={stateFilter.title}
                            onChange={(event): void => setStateFilter({ ...stateFilter, title: event.target.value })}
                            placeholder={i('alert.similar-cases.table.title.filter')}
                        />
                    ),
                    className: 'similar-cases-list__column__title column_preview',
                    render: (
                        data,
                        { tags: filterTags, customField: filterCustomField, severity: filterSeverity },
                    ): ReactElement => (
                        <CaseListTitle
                            data={data.case}
                            customFieldsIsOpen
                            tagsIsOpen
                            onSeverityClick={filterSeverity}
                            onTagClick={(tagValue, event): void => {
                                const isAltPressed = event.altKey;
                                filterTags([tagValue], isAltPressed);
                            }}
                            onCustomFieldsClick={(customField): void => filterCustomField(customField)}
                            onPreviewClick={() => {
                                eventList.onGetDetails(data.case._id).then((data) => {
                                    setData(data);
                                    setVisible(true);
                                });
                            }}
                        />
                    ),
                },
            ],
        },
        {
            title: [{ label: i('alert.similar-cases.table.created-at'), sortField: '_createdAt' }],
            className: 'similar-cases-list__column__date',
            children: [
                {
                    className: 'similar-cases-list__column__date',
                    render: ({ case: { _createdAt = 0 } }, { date: filterDate }): ReactElement => (
                        <FormatDate date={_createdAt} onClick={(): void => filterDate(_createdAt, '_createdAt')} />
                    ),
                },
            ],
        },
        {
            title: [{ label: i('alert.similar-cases.table.observables.label'), sortField: 'observableCount' }],
            className: 'similar-cases-list__column__obs-ioc',
            children: [
                {
                    className: 'similar-cases-list__column__obs-ioc',
                    title: (
                        <THInputNumber
                            defaultValue={0}
                            value={stateFilter.obsPercentage || undefined}
                            onChange={(value): void =>
                                setStateFilter({ ...stateFilter, obsPercentage: parseInt(value + '') || 0 })
                            }
                            placeholder={i('alert.similar-cases.table.observables.filter')}
                        />
                    ),
                    render: ({ similarObservableCount = 0, observableCount = 0 }): ReactElement => (
                        <AlertSimilarCasesStats
                            similar={similarObservableCount}
                            count={observableCount}
                            onClick={(percentage): void => {
                                setStateFilter({
                                    ...stateFilter,
                                    obsPercentage: percentage,
                                });
                            }}
                        />
                    ),
                },
            ],
        },
        {
            title: [{ label: i('alert.similar-cases.table.iocs.label'), sortField: 'iocCount' }],
            className: 'similar-cases-list__column__obs-ioc',
            children: [
                {
                    className: 'similar-cases-list__column__obs-ioc',
                    title: (
                        <THInputNumber
                            defaultValue={0}
                            value={stateFilter.iocPercentage || undefined}
                            onChange={(value): void =>
                                setStateFilter({ ...stateFilter, iocPercentage: parseInt(value + '') || 0 })
                            }
                            placeholder={i('alert.similar-cases.table.iocs.filter')}
                        />
                    ),
                    render: ({ similarIocCount = 0, iocCount = 0 }): ReactElement => (
                        <AlertSimilarCasesStats
                            similar={similarIocCount}
                            count={iocCount}
                            onClick={(percentage): void => {
                                setStateFilter({
                                    ...stateFilter,
                                    iocPercentage: percentage,
                                });
                            }}
                        />
                    ),
                },
            ],
        },
        {
            title: i('alert.similar-cases.table.matches.label'),
            className: 'similar-cases-list__column__matches',
            children: [
                {
                    className: 'similar-cases-list__column__matches',
                    title: (
                        <THTagSelector.Autocomplete
                            taxonomies={false}
                            tagRender={({ label }) => <THTag value={label} full textBlack />}
                            value={stateFilter.dataTypes}
                            placeholder={i('alert.similar-cases.table.matches.filter')}
                            dropdownRender={(menu): ReactElement => (
                                <div>
                                    <THButton
                                        icon={<ICONS.CheckIcon />}
                                        block
                                        onClick={(): void =>
                                            setStateFilter({ ...stateFilter, dataTypes: observableTypesList })
                                        }
                                        size="small"
                                    >
                                        {i('alert.similar-cases.table.matches.select-all')}
                                    </THButton>
                                    <THButton
                                        icon={<ICONS.CloseIcon />}
                                        block
                                        onClick={(): void => setStateFilter({ ...stateFilter, dataTypes: [] })}
                                        size="small"
                                    >
                                        {i('alert.similar-cases.table.matches.unselect-all')}
                                    </THButton>
                                    {menu}
                                </div>
                            )}
                            options={observableTypesList.map((observableType) => ({
                                label: observableType,
                                value: observableType,
                            }))}
                            onChange={(value): void => setStateFilter({ ...stateFilter, dataTypes: value })}
                            className="similar-cases-list__column__matches-select"
                        />
                    ),
                    render: ({ observableTypes }): ReactElement => (
                        <span>
                            {Object.entries(observableTypes).map(([key, value]) => (
                                <div key={key}>{`${key} (${value})`}</div>
                            ))}
                        </span>
                    ),
                },
            ],
        },
        {
            title: i('alert.similar-cases.table.actions.label'),
            className: 'similar-cases-list__column__actions',
            children: [
                {
                    className: 'similar-cases-list__column__actions',
                    title: (
                        <THButton
                            icon={<ICONS.CloseIcon />}
                            type="link"
                            onClick={() => setStateFilter(defaultStateFilterValues)}
                        >
                            {i('alert.similar-cases.table.actions.clear')}
                        </THButton>
                    ),
                    render: ({ case: { _id = '' } }): ReactElement | null =>
                        !alertData?.caseId ? (
                            <ActionsList
                                actionList={{
                                    merge: {
                                        label: i('alert.similar-cases.table.actions.merge'),
                                        query: () =>
                                            client.AlertController.mergeIntoCase({ alertId, caseId: _id })
                                                .then((): void => {
                                                    history.push(
                                                        RouteBuilder.compute('cases.details', { caseId: _id }),
                                                    );
                                                })
                                                .catch(handleNotificationError),
                                        icon: <ICONS.MergeIcon />,
                                    },
                                }}
                                scope="single"
                                isIcon
                            />
                        ) : null,
                },
            ],
        },
    ]);

    const filteredDataSource = useMemo(
        () =>
            dataSource
                .sort(
                    (
                        {
                            case: { title: titleA = '', _createdAt: _createdAtA = 0 },
                            observableCount: observableCountA = 0,
                            iocCount: iocCountA = 0,
                        },
                        {
                            case: { title: titleB = '', _createdAt: _createdAtB = 0 },
                            observableCount: observableCountB = 0,
                            iocCount: iocCountB = 0,
                        },
                    ): number => {
                        let sort = 0;
                        if ((sort = getFieldSort('title'))) {
                            return titleA.localeCompare(titleB) * -sort;
                        }
                        if ((sort = getFieldSort('_createdAt'))) {
                            return (_createdAtB - _createdAtA) * -sort;
                        }
                        if ((sort = getFieldSort('observableCount'))) {
                            return (observableCountB - observableCountA) * -sort;
                        }
                        if ((sort = getFieldSort('iocCount'))) {
                            return (iocCountB - iocCountA) * -sort;
                        }
                        return titleA.localeCompare(titleB) * -1;
                    },
                )
                .filter(
                    ({
                        case: { title = '' },
                        iocCount = 1,
                        similarIocCount,
                        observableCount = 1,
                        similarObservableCount,
                        observableTypes,
                    }) => {
                        const checkIoc =
                            Math.floor((similarIocCount / (iocCount || 1)) * 100) >= stateFilter.iocPercentage;

                        const checkObs =
                            Math.floor((similarObservableCount / (observableCount || 1)) * 100) >=
                            stateFilter.obsPercentage;

                        const hasDatatype = stateFilter.dataTypes.length
                            ? Object.keys(observableTypes).some((value) => stateFilter.dataTypes.includes(value))
                            : true;

                        return (
                            checkIoc &&
                            checkObs &&
                            hasDatatype &&
                            title.toLowerCase().includes(stateFilter.title.toLowerCase())
                        );
                    },
                )
                .splice(
                    (paginationManager?.page || 0) * (paginationManager?.itemsPerPage || 0),
                    paginationManager?.itemsPerPage || 0,
                ),
        [dataSource, stateFilter, getFieldSort, paginationManager],
    );

    const { data: describe } = useListAllFields();

    useEffect(() => {
        setAutoRefresh?.(localAutoRefresh);
    }, [localAutoRefresh]); // eslint-disable-line

    const setLocalFilters = (localFilters: boolean): void => {
        setLocalFilterIsOpen(localFilters);
        setFilterIsOpen?.(localFilters);
        filterManager.setEditFilters(localFilters);
    };

    useEffect(() => {
        setLocalFilterIsOpen(filterManager.editFilters);
    }, [filterManager.editFilters]); // eslint-disable-line

    useDrawer.watch(dataSource, () => {
        if (data && visible) {
            eventList.onGetDetails(data._id).then((data) => {
                setData(data);
            });
        }
    });

    useEffect(() => {
        if (customStatusList.length) {
            const caseStatusMap = customStatusList.reduce((acc, curr) => {
                if (![CaseStatusStage.NEW, CaseStatusStage.IN_PROGRESS].includes(curr.stage as CaseStatusStage)) {
                    return acc;
                }
                if (!acc[curr.stage]) {
                    acc[curr.stage] = [];
                }
                acc[curr.stage].push(curr);
                return acc;
            }, {} as CustomStatusMapType);
            setCaseStatus(caseStatusMap);
        }
    }, [customStatusList]);

    usePageTitle(i('page-title.alerts.similar-cases', { title: alertData?.title || '' }));

    return (
        <THLayout>
            <>
                <THList>
                    <THList.Header>
                        <THList.Slot name="MANAGE">
                            {storagePageManager && storageViewManager ? (
                                <ManageViewList
                                    pageManager={storagePageManager}
                                    viewManager={storageViewManager}
                                    beforeCreate={(content: any): any => {
                                        filterManager.applyFilters();
                                        return { ...content, filters: filterManager.tempFilters };
                                    }}
                                />
                            ) : null}
                            <QuickFilterList quickFilterList={quickFilterList} />
                        </THList.Slot>
                        <THList.Slot name="NAVIGATION">
                            {!storagePage?.autoRefresh && (
                                <Badge dot={needRefresh}>
                                    <THTooltip
                                        title={needRefresh ? i('common.refresh') : i('common.update-list')}
                                        placement="bottom"
                                    >
                                        <THButton
                                            size="small"
                                            type={needRefresh ? 'primary' : 'default'}
                                            shape="circle"
                                            onClick={() => {
                                                listRefetch();
                                                countRefetch();

                                                setNeedRefresh(false);
                                            }}
                                        >
                                            <ICONS.SyncOutlined />
                                        </THButton>
                                    </THTooltip>
                                </Badge>
                            )}
                            <ManageSwitch
                                label={i('common.auto-refresh')}
                                value={localAutoRefresh}
                                onChange={(autoRefresh) => setLocalAutoRefresh(autoRefresh)}
                                icon={<ICONS.SyncOutlined />}
                            />

                            <ManageSwitch
                                label={i('common.filters')}
                                value={localFilterIsOpen}
                                onChange={(editFilters): void => setLocalFilters(editFilters)}
                                icon={<ICONS.FilterOutlined />}
                            />
                        </THList.Slot>
                        <THList.Slot name="CONTENT">
                            {(localFilterIsOpen || !!filterManager.tempFilters.length) && (
                                <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.CASE]}>
                                    <Footer filterConfig={filterManager} />
                                </THFilter>
                            )}
                        </THList.Slot>
                    </THList.Header>

                    <THList.Content
                        dataSource={filteredDataSource.map((similar) => ({ ...similar, _thid: similar.case._id }))}
                        columns={columns}
                        rowKey="_thid"
                        locale={{
                            emptyText: (
                                <DisplayEmptyData spin={status === 'loading'} label={i('alert.similar-cases.none')} />
                            ),
                        }}
                    />

                    <THList.Footer>
                        <THList.Slot name="NAVIGATION">
                            <PaginationBrowse count={count} paginationConfig={paginationManager} />
                            <PaginationSize paginationConfig={paginationManager} />
                        </THList.Slot>
                    </THList.Footer>
                </THList>

                {data && visible && (
                    <CaseDrawer
                        data={data}
                        eventList={eventList}
                        actionList={caseActionList}
                        visible={visible}
                        afterVisible={setVisible}
                        onEditCounter={() => {
                            listRefetch();
                        }}
                    />
                )}
            </>
        </THLayout>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'alert.layout.nav-bar.similar-cases',
        icon: <ICONS.CaseIcon />,
    },
    path: RouteBuilder.compute('alerts.similar-cases'),
    content: <SimilarCasesList />,
    Context: ListProvider,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.alert.similar-cases' },
};
